// LandlordToolkitLanding.tsx - High-conversion landing page for non-authenticated users
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { ShieldCheckIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { updateSiteMetrics } from '../../utility/firebaseHelpers';
import { AvailableFeaturesSection } from '../../components/landlord-toolkit/AvailableFeatures';
import BookDemoCard from '../../components/landlord-toolkit/BookDemoCard';

const LandlordToolkitLanding: React.FC = () => {
  const navigate = useNavigate();
  const analytics = useAnalyticsWithConsent();
  const scrollMilestones = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Track Facebook Pixel & Reddit Pixel ViewContent event
    analytics.trackPixelViewContent({
      content_name: 'Landlord Toolkit Landing',
      content_ids: ['landlord-toolkit-landing'],
      content_type: 'product',
      value: 0,
      currency: 'EUR',
    });

    // Track landing view for Firebase Analytics
    analytics.trackCustomEvent('landlord_toolkit_landing_viewed', {
      page: 'landlord_toolkit_landing',
      timestamp: new Date().toISOString(),
    });
  }, [analytics]);

  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;

      const depth = Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
      const thresholds = [25, 50, 75, 100];

      thresholds.forEach((threshold) => {
        if (depth >= threshold && !scrollMilestones.current.has(threshold)) {
          scrollMilestones.current.add(threshold);
          analytics.trackCustomEvent('scroll_depth_reached', {
            depth_percent: threshold,
            page: 'landlord_toolkit_landing',
            timestamp: new Date().toISOString(),
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [analytics]);

  const stats = [
    { value: '100+', label: 'Properties Managed' },
    { value: '4.8/5', label: 'User Rating' },
    { value: '60%', label: 'Fewer Late Payments' },
  ];

  const testimonials = [
    {
      name: 'Sarah',
      role: 'Landlord, Dublin',
      image: 'https://ui-avatars.com/api/?name=Sarah+Murphy&background=2563eb&color=fff',
      quote:
        'The rent tracker has saved me hours every month. I can see all my properties at a glance and the automated reminders have reduced late payments significantly.',
      rating: 5,
    },
    {
      name: 'Michael',
      role: 'Property Manager, Cork',
      image: 'https://ui-avatars.com/api/?name=Michael+OBrien&background=2563eb&color=fff',
      quote:
        'Managing 15 properties was a nightmare before RentSmart. The toolkit makes everything so much easier. The broadcast feature alone is worth it!',
      rating: 5,
    },
    {
      name: 'Emma',
      role: 'Landlord, Galway',
      image: 'https://ui-avatars.com/api/?name=Emma+Collins&background=2563eb&color=fff',
      quote:
        "I was skeptical at first, but the free toolkit has genuinely transformed how I manage my rentals. It's professional and incredibly easy to use.",
      rating: 5,
    },
  ];

  const dashboardImageUrl =
    'https://firebasestorage.googleapis.com/v0/b/rentsmart-6e31b.appspot.com/o/assets%2Fmarketing-images%2Fframed-landlord-toolkit-dashboard-properties-view.png?alt=media&token=5aff99bb-abf3-4d09-af00-b4ffc7754269';

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-gradient-to-b from-blue-50 to-white overflow-visible">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white pt-20 pb-24 lg:pt-32 lg:pb-32">
        {/* Background Ambience - Deep, high-tech feel */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 mix-blend-overlay"></div>
        {/* Subtle colored light blobs for depth */}
        <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* LEFT COLUMN: The Copy & Conversion Engine */}
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              {/* Trust Badge - High Contrast */}
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/20 text-blue-300 px-3 py-1.5 rounded-full mb-6 backdrop-blur-md">
                <SparklesIcon className="w-4 h-4 text-yellow-300" />
                <span className="text-xs font-semibold tracking-wide uppercase">
                  100% Free to Start • No Credit Card Required
                </span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-extrabold mb-6 leading-tight tracking-tight text-white">
                Manage Your Irish Rentals, <br className="hidden lg:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-white to-yellow-100">
                  Compliance-Ready & Simple.
                </span>
              </h1>

              <p className="text-lg text-slate-300 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Stop worrying about RTB regulations and late rent. The all-in-one toolkit built
                specifically for Irish landlords to manage portfolios like a professional property
                manager.
              </p>

              {/* ACTION AREA: Clear Primary & Secondary CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                {/* PRIMARY CTA: High-Contrast, Action-Oriented */}
                <button
                  onClick={async () => {
                    analytics.trackInteraction('landlord_toolkit_cta_primary_clicked', {
                      location: 'hero',
                      button_text: 'Start Free Today',
                      variant: 'primary',
                    });

                    analytics.trackPixelCustom('landlord_toolkit_cta_primary_clicked', {
                      location: 'hero',
                      button_text: 'Start Free Today',
                      variant: 'primary',
                    });

                    // Track landlord toolkit lead signup
                    try {
                      await updateSiteMetrics({
                        landlordToolkitLeads: 1,
                      });
                    } catch (error) {
                      console.error('Failed to track lead:', error);
                    }
                    navigate('/landlord-toolkit/dashboard');
                  }}
                  className="group relative px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg transition-all shadow-xl shadow-blue-700/30 hover:shadow-2xl hover:-translate-y-0.5"
                >
                  Start Free Today
                  <ArrowRightIcon className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* SECONDARY CTA: Low-Friction, Education-Focused */}
                <button
                  onClick={() => {
                    analytics.trackInteraction('landlord_toolkit_feature_scroll', {
                      location: 'hero',
                      action: 'see_how_it_works',
                    });

                    analytics.trackPixelCustom('landlord_toolkit_feature_scroll', {
                      location: 'hero',
                      action: 'see_how_it_works',
                    });

                    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all backdrop-blur-sm"
                >
                  See How It Works
                </button>
              </div>

              {/* Trust Indicators (Micro-Social Proof) */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-8 text-sm text-slate-400 font-medium">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
                  <span>Zero Setup Time</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-emerald-400" />
                  <span>RTB & RPZ Friendly</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-300">★★★★★</span>
                  <span>Local Irish Support</span>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: The "Product Proof" (Actual Image) */}
            <div className="w-full lg:w-1/2 relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-3xl lg:max-w-none">
                <img
                  src={dashboardImageUrl}
                  alt="Landlord Toolkit Dashboard inside a Macbook frame showing property, tenant, and finance data for Irish rentals."
                  className="w-full h-auto rounded-lg transition-all duration-700 ease-out 
                           
                           lg:scale-[1.02] lg:hover:scale-100 
                           lg:translate-y-2 lg:hover:translate-y-0"
                />
              </div>
              {/* Floating AI Teaser (Replaces the big box) */}
              <div className="absolute -right-4 -bottom-4 bg-gradient-to-br from-indigo-600 to-blue-700 p-4 rounded-xl shadow-xl border border-white/10 max-w-[200px]">
                <div className="flex items-start gap-3">
                  <SparklesIcon className="w-8 h-8 text-yellow-300 shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-white mb-1">AI Assistant</div>
                    <div className="text-[10px] text-blue-100 leading-tight">
                      Analyzing market rates for your renewal...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Spotlight */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 via-white to-blue-50 border-y border-gray-200">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold mb-3">
                <CheckCircleIcon className="w-5 h-5" />
                Compliance without the headache
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Stay ahead of Irish regulations with our toolkit.
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Auto-track rent caps in RPZs, keep RTB notice periods front and centre, and log
                every tenancy action so you are audit-ready.
              </p>
              <div className="space-y-3 text-gray-700">
                <div className="flex gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                  <p className="leading-relaxed">
                    Guided workflows for rent reviews and notices that align with RTB requirements.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                  <p className="leading-relaxed">
                    RPZ rent calculator and history log so you never exceed the cap.
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
                  <p className="leading-relaxed">
                    Documented audit trail for notices, tenant comms, and rent receipts—exportable
                    on demand.
                  </p>
                </div>
              </div>
              <button
                onClick={async () => {
                  analytics.trackInteraction('landlord_toolkit_cta_compliance', {
                    section: 'compliance_spotlight',
                    button_text: 'Start free—stay compliant',
                  });

                  analytics.trackPixelCustom('landlord_toolkit_cta_compliance', {
                    section: 'compliance_spotlight',
                    button_text: 'Start free—stay compliant',
                  });

                  // Track landlord toolkit lead
                  try {
                    await updateSiteMetrics({
                      landlordToolkitLeads: 1,
                    });
                  } catch (error) {}

                  navigate(`/signup?returnUrl=${encodeURIComponent('/landlord-toolkit')}`);
                }}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all shadow-sm"
              >
                Start free—stay compliant
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-500">RPZ Rent Review</p>
                  <p className="text-2xl font-bold text-gray-900">€1,650 → €1,705</p>
                </div>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                  Compliant
                </span>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-center justify-between">
                  <span>Cap applied (4% rule)</span>
                  <span className="font-semibold text-emerald-700">Ok</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Notice period</span>
                  <span className="font-semibold text-emerald-700">90 days set</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Tenant communication</span>
                  <span className="font-semibold text-emerald-700">Logged</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Audit pack</span>
                  <span className="font-semibold text-emerald-700">Ready to export</span>
                </div>
              </div>
              <div className="mt-6 rounded-xl bg-gradient-to-br from-emerald-50 to-white p-4 text-xs text-gray-600 border border-emerald-100">
                Built with Irish RTB & RPZ guidance so you avoid costly mistakes and keep tenants
                informed.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Performance Coming Soon */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-blue-100 text-sm font-semibold mb-3 border border-white/10">
                <SparklesIcon className="w-5 h-5 text-amber-300" />
                AI property performance • Coming soon
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Smarter insights with AI to keep every tenancy profitable — coming soon.
              </h2>
              <p className="text-lg text-blue-100/90 mb-6 leading-relaxed">
                Coming soon: an AI layer that surfaces arrears risk before month-end, spots expense
                drift early, benchmarks rent against your local market, and drafts compliant actions
                so issues get fixed before they become costly.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  {
                    title: 'Performance score per property',
                    copy: 'Occupancy risk, arrears likelihood, and rent delta vs market in one glance.',
                  },
                  {
                    title: 'Expense drift alerts',
                    copy: 'Flags spend creep on cleaning, utilities, and maintenance before renewal.',
                  },
                  {
                    title: 'Next-best action',
                    copy: 'Suggested notice timelines, reminders, and comms drafts tailored to RTB rules.',
                  },
                  {
                    title: 'Scenario planning',
                    copy: 'Model cash flow if you change rent, refinance, or rotate tenants.',
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-white/10 bg-white/5 p-4 shadow-sm hover:border-blue-200/40 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 rounded-full bg-white/10 p-2 text-blue-100">
                        <ChartBarIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-white mb-1">{item.title}</div>
                        <p className="text-sm text-blue-100/80 leading-relaxed">{item.copy}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={() => {
                    analytics.trackInteraction('landlord_toolkit_ai_performance_waitlist', {
                      location: 'ai_performance_section',
                      button_text: 'Reserve free AI access',
                    });
                    navigate(
                      `/signup?returnUrl=${encodeURIComponent('/landlord-toolkit')}&intent=ai-performance`
                    );
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg"
                >
                  Reserve free AI access
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    analytics.trackInteraction('landlord_toolkit_ai_start_free', {
                      location: 'ai_performance_section',
                      button_text: 'Start Free Today',
                    });
                    navigate(`/signup?returnUrl=${encodeURIComponent('/landlord-toolkit')}`);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-all"
                >
                  Start Free Today
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
                <div className="text-sm text-blue-100/80">
                  Start with Toolkit now and you will get the AI tools the moment they launch—no
                  extra cost, just a heads-up when we go live.
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 shadow-2xl backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm text-blue-100/80">Portfolio signal</p>
                  <p className="text-3xl font-bold text-white">Optimised</p>
                </div>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-emerald-300/20 text-emerald-100 border border-emerald-100/30">
                  Coming soon
                </span>
              </div>
              <div className="space-y-4 text-sm text-blue-50">
                <div className="flex items-center justify-between">
                  <span>Arrears risk (next 30 days)</span>
                  <span className="font-semibold text-amber-200">Low • 6%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Rent vs market benchmark</span>
                  <span className="font-semibold text-emerald-200">+3.5% headroom</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Maintenance drift</span>
                  <span className="font-semibold text-amber-200">Watch • Cleaning +12%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Occupancy health</span>
                  <span className="font-semibold text-emerald-200">Stable • 98%</span>
                </div>
                <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-4 text-blue-100/80">
                  Upcoming: AI-generated action list to protect cash flow, aligned with RTB and RPZ
                  rules.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white" id="how-it-works">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-xl text-gray-600">
              Start managing your properties professionally in under 5 minutes
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  title: 'Create Your Account',
                  description: 'Sign up for free in under 60 seconds. No credit card required.',
                  icon: UserGroupIcon,
                },
                {
                  step: '02',
                  title: 'Add Your Properties',
                  description:
                    "Easily add your properties and tenants. We'll guide you through every step.",
                  icon: ClipboardDocumentListIcon,
                },
                {
                  step: '03',
                  title: 'Start Managing',
                  description:
                    'Track payments, send reminders, and communicate with tenants effortlessly.',
                  icon: ChartBarIcon,
                },
              ].map((item, index) => (
                <div key={index} className="relative">
                  {index < 2 && (
                    <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent -translate-x-1/2 z-0" />
                  )}

                  <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border-2 border-blue-100 hover:border-blue-300 transition-all">
                    <div className="text-6xl font-bold text-blue-100 mb-4">{item.step}</div>
                    <div className="mb-4">
                      <item.icon className="w-12 h-12 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex justify-center">
              <button
                onClick={() => {
                  analytics.trackInteraction('landlord_toolkit_cta_how_it_works', {
                    location: 'how_it_works_section',
                    button_text: 'Start Free Today',
                  });
                  navigate(`/signup?returnUrl=${encodeURIComponent('/landlord-toolkit')}`);
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-700/30"
              >
                Start Free Today
                <ArrowRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Viewing Management Feature Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column: Benefits & Copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-semibold mb-4 border border-purple-200">
                <CalendarIcon className="w-5 h-5" />
                New Feature • Zero Setup
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Stop the back-and-forth. Book viewings in seconds.
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Say goodbye to endless WhatsApp messages and double bookings. Our viewing management
                system keeps your calendar organized, tracks every prospect, and sends automatic
                reminders—so you never miss a viewing again.
              </p>

              {/* Benefit Points */}
              <div className="space-y-4 mb-8">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                    <CheckCircleIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      Visual calendar with one-click booking
                    </p>
                    <p className="text-sm text-gray-600">
                      See all your viewings at a glance. Click any date to add a new viewing
                      instantly.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                    <CheckCircleIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      Track every prospect automatically
                    </p>
                    <p className="text-sm text-gray-600">
                      Name, email, phone—all saved. Contact prospects with one click from the
                      dashboard.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                    <CheckCircleIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Status tracking for every viewing</p>
                    <p className="text-sm text-gray-600">
                      Mark as pending, confirmed, attended, or no-show. Know exactly where you
                      stand.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-1">
                    <CheckCircleIcon className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Real-time stats dashboard</p>
                    <p className="text-sm text-gray-600">
                      See upcoming viewings, total prospects, and conversion rates in one place.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={async () => {
                  analytics.trackInteraction('landlord_toolkit_cta_viewing_management', {
                    section: 'viewing_management_feature',
                    button_text: 'Start Managing Viewings Free',
                  });

                  analytics.trackPixelCustom('landlord_toolkit_cta_viewing_management', {
                    section: 'viewing_management_feature',
                    button_text: 'Start Managing Viewings Free',
                  });

                  // Track landlord toolkit lead
                  try {
                    analytics.trackPixelCustom('Lead', {
                      content_name: 'Landlord Toolkit - Viewing Management',
                      content_category: 'landlord_toolkit',
                    });
                  } catch (error) {
                    console.error('Pixel tracking error:', error);
                  }

                  navigate(
                    `/signup?returnUrl=${encodeURIComponent('/landlord-toolkit/manage-viewings')}`
                  );
                }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-purple-600/30 hover:shadow-xl hover:-translate-y-0.5"
              >
                Start Managing Viewings Free
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <p className="text-sm text-gray-500 mt-3">
                ✓ No credit card • ✓ 5-minute setup
              </p>
            </div>

            {/* Right Column: Visual Demo */}
            <div className="relative">
              {/* Main Dashboard Card */}
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold">Viewing Dashboard</h3>
                      <p className="text-sm text-purple-100">This Week</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm font-semibold">
                      Live
                    </div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="p-6 bg-gradient-to-b from-purple-50 to-white">
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-white p-4 rounded-lg border border-purple-100 shadow-sm">
                      <div className="text-2xl font-bold text-purple-600">8</div>
                      <div className="text-xs text-gray-600 font-medium">Upcoming</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-blue-100 shadow-sm">
                      <div className="text-2xl font-bold text-blue-600">24</div>
                      <div className="text-xs text-gray-600 font-medium">Prospects</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-emerald-100 shadow-sm">
                      <div className="text-2xl font-bold text-emerald-600">12</div>
                      <div className="text-xs text-gray-600 font-medium">Completed</div>
                    </div>
                  </div>

                  {/* Mini Calendar Preview */}
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-gray-900">January 2026</h4>
                      <CalendarIcon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                        <div key={i} className="text-gray-500 font-medium py-1">
                          {day}
                        </div>
                      ))}
                      {[...Array(31)].map((_, i) => (
                        <div
                          key={i}
                          className={`py-1.5 rounded ${
                            [5, 12, 18].includes(i)
                              ? 'bg-purple-100 text-purple-700 font-bold'
                              : 'text-gray-700'
                          }`}
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Upcoming Viewing */}
                  <div className="mt-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-bold text-gray-900">John Smith</p>
                        <p className="text-xs text-gray-600">2 Bed Apt, Dublin 4</p>
                      </div>
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                        Confirmed
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="w-3.5 h-3.5" />
                        Today, 6:00 PM
                      </div>
                      <div className="flex items-center gap-1">
                        <EnvelopeIcon className="w-3.5 h-3.5" />
                        john@email.com
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white px-4 py-2 rounded-xl shadow-xl border-2 border-white font-bold text-sm">
                ✨ Live Now
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-2 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-2">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Rentals and Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful tools designed specifically for Irish landlords. Start with our free
              features.
            </p>
          </div>

          {/* Available Tools from toolkit components */}
          <div className="mt-16">
            <AvailableFeaturesSection />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-2">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Loved by Landlords Across Ireland
            </h2>
            <p className="text-xl text-gray-600">
              See what other landlords are saying about RentSmart Toolkit
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-200"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>

                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.quote}"</p>

                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Demo Section */}
      <section className="py-3 bg-white">
        <div className="container mx-auto px-4">
          <div className="mx-auto">
            <BookDemoCard />
          </div>
        </div>
      </section>

      <section className="py-2 bg-white">
        <div className="container mx-auto px-4">
          <div className="overflow-hidden ">
            <img
              src={dashboardImageUrl}
              alt="Preview of the RentSmart landlord dashboard with property and rent tracking."
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Rental Management?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of Irish landlords who are saving time and reducing late payments with
            RentSmart Toolkit.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={async () => {
                analytics.trackInteraction('landlord_toolkit_cta_final', {
                  location: 'final_cta_section',
                  button_text: 'Start Your Free Account',
                  variant: 'primary',
                });

                // Track landlord toolkit lead
                try {
                  await updateSiteMetrics({
                    landlordToolkitLeads: 1,
                  });
                } catch (error) {
                  console.error('Failed to track lead:', error);
                }

                navigate(`/signup?returnUrl=${encodeURIComponent('/landlord-toolkit')}`);
              }}
              className="px-10 py-5 bg-white text-blue-600 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Start Your Free Account
              <ArrowRightIcon className="inline-block w-6 h-6 ml-2" />
            </button>
          </div>

          <p className="text-sm text-blue-200">
            No credit card required • Free forever plan available • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
};

export default LandlordToolkitLanding;
